import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ShieldCheck, ChevronRight, Target, Clock, Award, 
  BookOpen, RotateCcw, CheckCircle, Lock, CreditCard
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { ModuleCard } from '../components/course/ModuleCard';
import { courseModules } from '../data/courseData';
import { useProgress } from '../context/ProgressContext';
import { usePurchase } from '../context/PurchaseContext';

export default function HomePage() {
  const { getOverallProgress, progress, resetProgress } = useProgress();
  const { hasPurchased } = usePurchase();
  const overallProgress = getOverallProgress();
  const hasStarted = progress.completedModules.length > 0;

  return (
    <div className="max-w-6xl mx-auto fade-in">
      {/* Hero Section */}
      <section className="relative mb-12">
        <div className="bg-gradient-to-br from-primary to-primary-700 rounded-3xl overflow-hidden">
          <div className="relative p-8 md:p-12 lg:p-16">
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-secondary rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full blur-3xl" />
            </div>
            
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <ShieldCheck className="w-4 h-4" />
                  <span>Australian Digital Safety Course</span>
                </div>
                
                <h1 className="font-heading font-extrabold text-4xl md:text-5xl lg:text-6xl text-white mb-6 leading-tight">
                  Stay Safe Online, <span className="text-secondary">Step by Step</span>
                </h1>
                
                <p className="text-primary-100 text-lg md:text-xl leading-relaxed mb-8">
                  Learn practical digital safety skills designed for everyday Australians. 
                  No technical jargon—just simple habits that keep you protected.
                </p>

                <div className="flex flex-wrap gap-4">
                  {hasPurchased ? (
                    // Purchased user - show continue/start learning
                    hasStarted ? (
                      <>
                        <Link to={`/module/${courseModules[Math.max(0, (progress.currentModule || 1) - 1)]?.slug || 'introduction'}`}>
                          <Button 
                            data-testid="continue-learning"
                            className="bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full px-8 py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all"
                          >
                            Continue Learning
                            <ChevronRight className="w-5 h-5 ml-2" />
                          </Button>
                        </Link>
                        <Button
                          data-testid="reset-progress"
                          variant="outline"
                          onClick={() => {
                            if (window.confirm('Are you sure you want to reset all progress?')) {
                              resetProgress();
                            }
                          }}
                          className="border-white/30 text-white hover:bg-white/10 rounded-full px-6 py-6 font-semibold"
                        >
                          <RotateCcw className="w-4 h-4 mr-2" />
                          Start Over
                        </Button>
                      </>
                    ) : (
                      <Link to="/module/introduction">
                        <Button 
                          data-testid="get-started"
                          className="bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full px-8 py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all"
                        >
                          Start Learning
                          <ChevronRight className="w-5 h-5 ml-2" />
                        </Button>
                      </Link>
                    )
                  ) : (
                    // Not purchased - show purchase and free trial options
                    <>
                      <Link to="/purchase">
                        <Button 
                          data-testid="purchase-course"
                          className="bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full px-8 py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all"
                        >
                          <CreditCard className="w-5 h-5 mr-2" />
                          Get Full Access - $6.99
                        </Button>
                      </Link>
                      <Link to="/module/introduction">
                        <Button 
                          data-testid="try-free"
                          variant="outline"
                          className="border-white/30 text-white hover:bg-white/10 rounded-full px-6 py-6 font-semibold"
                        >
                          Try Module 1 Free
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    </>
                  )}
                </div>
              </div>

              <div className="hidden md:block">
                <img
                  src="https://images.pexels.com/photos/3781932/pexels-photo-3781932.jpeg"
                  alt="Smiling seniors using a tablet together"
                  className="rounded-2xl shadow-2xl object-cover w-full h-80 lg:h-96"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Purchase CTA for non-purchasers */}
      {!hasPurchased && (
        <section className="mb-12 bg-gradient-to-r from-secondary/10 to-secondary/5 rounded-2xl border border-secondary/30 p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h2 className="font-heading font-bold text-xl text-text mb-2">
                🔓 Unlock All 12 Modules
              </h2>
              <p className="text-text-muted">
                Get lifetime access to the complete course, interactive quizzes, and your certificate.
              </p>
            </div>
            <Link to="/purchase">
              <Button className="bg-primary text-white hover:bg-primary-600 rounded-full px-6 py-4 font-bold whitespace-nowrap">
                Purchase for $6.99 AUD
              </Button>
            </Link>
          </div>
        </section>
      )}

      {/* Progress section (if started and purchased) */}
      {hasStarted && hasPurchased && (
        <section className="mb-12 bg-white rounded-2xl border-2 border-stone-200 p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="font-heading font-bold text-2xl text-text mb-1">Your Progress</h2>
              <p className="text-text-muted">
                {progress.completedModules.length} of {courseModules.length} modules completed
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-4xl font-heading font-bold text-primary">{overallProgress}%</p>
                <p className="text-text-muted text-sm">Complete</p>
              </div>
              {overallProgress === 100 && (
                <Link to="/certificate">
                  <Button className="bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full px-6 py-4 font-bold">
                    <Award className="w-5 h-5 mr-2" />
                    View Certificate
                  </Button>
                </Link>
              )}
            </div>
          </div>
          
          <div className="h-4 bg-stone-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-primary to-primary-400 rounded-full transition-all duration-500 progress-fill"
              style={{ width: `${overallProgress}%` }}
            />
          </div>
        </section>
      )}

      {/* Features */}
      <section className="mb-12 grid md:grid-cols-3 gap-6">
        {[
          {
            icon: Target,
            title: '12 Modules',
            description: 'Covering passwords, scams, privacy, and more',
            color: 'primary',
          },
          {
            icon: Clock,
            title: 'Self-Paced',
            description: 'Learn at your own speed, on any device',
            color: 'secondary',
          },
          {
            icon: Award,
            title: 'Certificate',
            description: 'Earn a certificate when you complete the course',
            color: 'success',
          },
        ].map((feature, index) => (
          <div
            key={index}
            className="bg-white rounded-2xl border border-stone-200 p-6 text-center card-hover"
          >
            <div className={`w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center ${
              feature.color === 'primary' ? 'bg-primary/10' :
              feature.color === 'secondary' ? 'bg-secondary/20' :
              'bg-success/10'
            }`}>
              <feature.icon className={`w-8 h-8 ${
                feature.color === 'primary' ? 'text-primary' :
                feature.color === 'secondary' ? 'text-secondary-700' :
                'text-success'
              }`} />
            </div>
            <h3 className="font-heading font-bold text-lg text-text mb-2">{feature.title}</h3>
            <p className="text-text-muted text-sm">{feature.description}</p>
          </div>
        ))}
      </section>

      {/* All Modules */}
      <section>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-text mb-2">
              Course Modules
            </h2>
            <p className="text-text-muted">
              {hasPurchased 
                ? 'Complete each module to build your digital safety skills'
                : 'Module 1 is free • Purchase to unlock all modules'
              }
            </p>
          </div>
          <div className="hidden md:flex items-center gap-2 text-text-muted">
            {hasPurchased ? (
              <>
                <CheckCircle className="w-5 h-5 text-success" />
                <span className="text-sm">{progress.completedModules.length} completed</span>
              </>
            ) : (
              <>
                <Lock className="w-5 h-5 text-stone-400" />
                <span className="text-sm">11 modules locked</span>
              </>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courseModules.map((module) => (
            <ModuleCard key={module.id} module={module} />
          ))}
        </div>
      </section>

      {/* Footer CTA */}
      <section className="mt-16 text-center">
        <div className="bg-gradient-to-br from-secondary-50 to-secondary-100 rounded-3xl p-8 md:p-12 border border-secondary/30">
          <img 
            src="https://customer-assets.emergentagent.com/job_aussiesafety/artifacts/17uanq9m_BTA_Logo_NoBgnd.png"
            alt="Bellarine Tech Assist Logo"
            className="w-24 h-24 mx-auto mb-6 object-contain"
          />
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-text mb-4">
            Based on the Digital Safety Workbook
          </h2>
          <p className="text-text-muted max-w-2xl mx-auto mb-6">
            This course is based on the "Digital Safety Workbook" by Bellarine Tech Assist, 
            designed to help everyday Australians stay safe, confident, and connected online.
          </p>
          <p className="text-sm text-text-muted">
            © {new Date().getFullYear()} Bellarine Tech Assist • For educational purposes
          </p>
        </div>
      </section>
    </div>
  );
}
